import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { NamedIcon } from '@shared/ui/icons';

interface Reel { handle: string; views: number | null; likes: number | null; er: number | null; thumb: string | null; permalink: string | null }
type Sort = 'views' | 'er';

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

// Global "top reels in KG" — inspiration feed. Toggle: biggest reach (views) vs most
// engaging per view. The RPC picks an adaptive window (scraping runs in weekly batches,
// so a strict 7-day window is routinely empty) and floors ER by a percentile of that
// window, so neither sort comes back empty.
export default function TopReelsWeek({ limit = 3, bare = false }: { limit?: number; bare?: boolean } = {}) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });
  const [sort, setSort] = useState<Sort>('views');
  const q = useSupabaseQuery<Reel[]>(['top-reels-week', sort, limit], async () => supabase.rpc('get_top_reels_week', { p_limit: limit, p_sort: sort }));

  const rows = q.data ?? [];
  // Only hide the block entirely when the default view has nothing at all. Once the
  // user has switched tabs, keep it mounted — a section vanishing under the tap reads
  // as a broken app.
  if (!rows.length && !q.isLoading && sort === 'views') return null;

  const tab = (s: Sort, label: string) => (
    <button type="button" onClick={() => setSort(s)}
      style={{ background: sort === s ? 'rgba(74,158,255,0.16)' : 'transparent', border: '1px solid ' + (sort === s ? 'rgba(74,158,255,0.4)' : 'rgba(255,255,255,0.12)'),
        color: sort === s ? 'var(--accent-text)' : 'var(--dim)', borderRadius: 20, padding: bare ? '8px 14px' : '3px 10px', fontSize: bare ? 13 : 11, cursor: 'pointer', fontFamily: 'inherit' }}>
      {label}
    </button>
  );

  return (
    <div style={{ marginTop: bare ? 0 : 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: bare ? 'flex-start' : 'space-between', margin: '0 2px 8px' }}>
        {!bare && <span style={{ fontSize: 12, color: 'var(--dim)' }}>{tr('creator.home.topReels', 'Топ рилс недели в Кыргызстане')}</span>}
        <div style={{ display: 'flex', gap: 6 }}>
          {tab('views', tr('creator.home.byViews', 'Просмотры'))}
          {tab('er', tr('creator.home.byEngage', 'Вовлечённость'))}
        </div>
      </div>
      {!rows.length && !q.isLoading && (
        <div style={{ color: 'var(--dim)', fontSize: 12, padding: '14px 2px' }}>
          {tr('creator.home.reelsEmpty', 'Пока нет рилсов с достаточным охватом для этого среза.')}
        </div>
      )}
      <div className="cr-reels-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {rows.map((r, i) => (
          <a key={i} href={r.permalink || '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <div style={{ position: 'relative', aspectRatio: '3 / 4', borderRadius: 10, overflow: 'hidden', background: '#161a24', border: '1px solid rgba(255,255,255,0.08)' }}>
              {r.thumb && <img src={r.thumb} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '16px 6px 5px', background: 'linear-gradient(transparent, rgba(0,0,0,0.82))', display: 'flex', alignItems: 'center', gap: 4 }}>
                {sort === 'er'
                  ? <><NamedIcon name="heart" size={11} color="#fff" /><span style={{ color: '#fff', fontSize: 11.5, fontWeight: 600 }}>{r.er != null ? `${r.er}%` : '—'}</span><span style={{ color: '#bcb', fontSize: 11, marginLeft: 2 }}>· {fmtK(r.views)}</span></>
                  : <><NamedIcon name="eye" size={11} color="#fff" /><span style={{ color: '#fff', fontSize: 11.5, fontWeight: 600 }}>{fmtK(r.views)}</span></>}
              </div>
            </div>
            <div style={{ color: 'var(--accent-text)', fontSize: 11, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{r.handle}</div>
          </a>
        ))}
      </div>
    </div>
  );
}
